import moment from "moment";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import type { SnackbarSlice } from "@/store/slices/types/snackbar";
import {
  GetFilteredLendersProps,
  IDispatchResponse,
  ReportDateRange,
  ReportExportPayload,
  ScrollToElementByIdProps,
} from "./interface";

export const getFilteredLenders = ({
  applicationType,
  permittedLenders,
  lenderList,
  isProducts = false,
  isSorted = false,
}: GetFilteredLendersProps) => {
  const lendersPermission = permittedLenders?.lenders;
  const keys = Object.keys(lendersPermission);

  const permission = keys.filter(function (key) {
    return lendersPermission[key];
  });

  let filteredLenderList = [];

  if (applicationType === ApplicationType.Consumer) {
    filteredLenderList = lenderList.filter((ele) => {
      const lenderRowName = isProducts ? ele.lender : ele;
      const lenderName = lenderRowName.toLowerCase().replaceAll("&", "and").replaceAll(" - ", "_").replaceAll(" ", "_");
      if (permission.includes(lenderName)) {
        return ele;
      }
    });
  } else if (applicationType === ApplicationType.Personal) {
    filteredLenderList = lenderList.filter((ele) => {
      const lenderRowName = isProducts ? ele.lender : ele;
      const lenderName = lenderRowName.toLowerCase().replaceAll("&", "and").replaceAll(" - ", "_").replaceAll(" ", "_");
      if (permission.includes(lenderName)) {
        return ele;
      }
    });
  } else if (applicationType === ApplicationType.Business) {
    filteredLenderList = lenderList.filter((ele) => {
      const lenderRowName = isProducts ? ele.lender : ele;
      const lenderName = lenderRowName.toLowerCase().replaceAll("&", "and").replaceAll(" - ", "_").replaceAll(" ", "_");
      if (permission.includes(lenderName)) {
        return ele;
      }
    });
  } else {
    filteredLenderList = lenderList.filter((ele) => {
      const lenderRowName = isProducts ? ele.lender : ele;
      const lenderName = lenderRowName.toLowerCase().replaceAll("&", "and").replaceAll(" - ", "_").replaceAll(" ", "_");
      if (permission.includes(lenderName)) {
        return ele;
      }
    });
  }

  // find unique lenders and remove duplicates
  filteredLenderList = Array.from(new Set(filteredLenderList));

  if (isSorted) {
    // sort lenders
    filteredLenderList = filteredLenderList?.flat()?.sort();
  }

  return filteredLenderList;
};

export const scrollToElementById = ({ elementId, block = "center" }: ScrollToElementByIdProps) => {
  if (!elementId) return;
  document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block });
};

/**
 * Builds the DOM id for an asset field input so "Requires ..." hints can scroll to it.
 * Assets render side by side, so the id has to be scoped by asset.
 */
export const getAssetFieldAnchorId = (fieldName: string, assetId?: string) =>
  assetId ? `${fieldName}_${assetId}` : "";

/** Empty, missing and zero all mean the provider had nothing to say — such a row is not worth a line. */
export const hasValue = (value?: string | number) => {
  if (value == null || value === "") return false;
  const numeric = typeof value === "number" ? value : parseFloat(value);
  return Number.isNaN(numeric) ? true : numeric !== 0;
};

export const buildReportDateQuery = ({ fromDate, toDate }: ReportDateRange) => {
  const formattedFromDate = moment(fromDate).format("YYYY-MM-DD");
  const formattedToDate = moment(toDate).format("YYYY-MM-DD");

  if (formattedFromDate === "Invalid date" || formattedToDate === "Invalid date") {
    return "";
  }

  return `?${new URLSearchParams({
    fromDate: formattedFromDate,
    toDate: formattedToDate,
  }).toString()}`;
};

export const buildReportRequestBody = ({ fromDate, toDate, email }: ReportExportPayload) => ({
  fromDate: moment(fromDate).format("YYYY-MM-DD"),
  toDate: moment(toDate).format("YYYY-MM-DD"),
  email: email.trim(),
});

export const getReportDownloadUrl = (res: IDispatchResponse) => {
  const reportData = res?.payload?.data?.data as { url?: unknown } | undefined;

  return typeof reportData?.url === "string" ? reportData.url : undefined;
};

export const getToastSuccessMessage = (res: IDispatchResponse, fallbackMessage: string) => {
  return res?.payload?.data?.status_message || fallbackMessage;
};

export const getToastErrorMessage = (res: IDispatchResponse, fallbackMessage: string) => {
  return res?.payload?.status_message || fallbackMessage;
};

export const getSuccessToastPayload = (
  res: IDispatchResponse,
  fallbackMessage: string,
): Pick<SnackbarSlice, "message" | "intent"> => ({
  message: getToastSuccessMessage(res, fallbackMessage),
  intent: "success",
});

export const getErrorToastPayload = (
  res: IDispatchResponse,
  fallbackMessage: string,
): Pick<SnackbarSlice, "message" | "intent"> => ({
  message: getToastErrorMessage(res, fallbackMessage),
  intent: "danger",
});
